import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAllEvents } from "../../../../services/eventServices";
import { getUsersEvents } from "../../../../services/usersEventsServices";
import { getSettings } from "../../../../services/settingsServices";

import {
  BASE_EVENT_TYPES,
  DEFAULT_UI_STATE,
  FIXED_EVENT_CATEGORIES,
  FIXED_EVENT_LEVELS,
  collatorFR,
  getEventCategories,
  getEventDate,
  getUserId,
  isActiveEvent,
  isEventPast,
  strip,
} from "../utils/calendarEventsUtils";

const STORAGE_KEY = "calendar_events_dashboard_ui";

export function useCalendarEvents() {
  const userId = getUserId();

  const [events, setEvents] = useState([]);
  const [favoriteEvents, setFavoriteEvents] = useState([]);
  const [shortnameClub, setShortnameClub] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [ui, setUi] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      return saved ? { ...DEFAULT_UI_STATE, ...saved } : DEFAULT_UI_STATE;
    } catch {
      return DEFAULT_UI_STATE;
    }
  });

  const [debouncedSearch, setDebouncedSearch] = useState(ui.search || "");

  const persistUi = useCallback((next) => {
    setUi(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const updateUi = useCallback(
    (patch) => {
      const shouldResetPage = [
        "search",
        "category",
        "type",
        "level",
        "status",
        "favoriteOnly",
        "sort",
      ].some((key) => Object.prototype.hasOwnProperty.call(patch, key));

      persistUi({
        ...ui,
        ...patch,
        perPage: shouldResetPage
          ? DEFAULT_UI_STATE.perPage
          : patch.perPage ?? ui.perPage,
      });
    },
    [persistUi, ui]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      persistUi({
        ...ui,
        search: debouncedSearch,
        perPage: DEFAULT_UI_STATE.perPage,
      });
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [settingsData, eventsData, favoritesData] = await Promise.all([
        getSettings().catch(() => []),
        getAllEvents(),
        userId ? getUsersEvents(userId).catch(() => []) : Promise.resolve([]),
      ]);

      const settings = Array.isArray(settingsData) ? settingsData[0] : null;
      setShortnameClub(settings?.shortname_club || "");

      const activeEvents = Array.isArray(eventsData)
        ? eventsData.filter(isActiveEvent)
        : [];

      setEvents(activeEvents);

      const favoriteIds = Array.isArray(favoritesData)
        ? favoritesData.map((fav) => fav.event_id)
        : [];

      setFavoriteEvents(favoriteIds);
    } catch (err) {
      console.error("Erreur chargement calendrier :", err);
      setError("Impossible de charger les événements pour le moment.");
      toast.error("Erreur lors du chargement du calendrier.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const typeOptions = useMemo(() => {
    const clubType = shortnameClub || "Votre Club";

    const fromEvents = events.map((event) => event?.type_event).filter(Boolean);

    return Array.from(new Set([clubType, ...BASE_EVENT_TYPES, ...fromEvents]))
      .filter(Boolean)
      .sort((a, b) => collatorFR.compare(a, b));
  }, [events, shortnameClub]);

  const categoryOptions = useMemo(() => {
    const fromEvents = events.flatMap(getEventCategories);

    return Array.from(new Set([...FIXED_EVENT_CATEGORIES, ...fromEvents]))
      .filter(Boolean)
      .sort((a, b) => collatorFR.compare(a, b));
  }, [events]);

  const levelOptions = useMemo(() => {
    const fromEvents = events.map((event) => event?.level_event).filter(Boolean);

    return Array.from(new Set([...FIXED_EVENT_LEVELS, ...fromEvents]))
      .filter(Boolean)
      .sort((a, b) => collatorFR.compare(a, b));
  }, [events]);

  const filteredEvents = useMemo(() => {
    const search = strip(ui.search);

    const list = events.filter((event) => {
      const eventStart = getEventDate(event?.start);

      if (!eventStart) return false;

      const eventIsPast = isEventPast(event);
      const categories = getEventCategories(event);

      const haystack = strip(
        [
          event?.titre,
          event?.description,
          event?.lieu_event,
          event?.type_event,
          event?.level_event,
          event?.cate_event,
        ]
          .filter(Boolean)
          .join(" ")
      );

      const matchSearch = !search || haystack.includes(search);

      const matchCategory =
        ui.category === "all" || categories.includes(ui.category);

      const matchType = ui.type === "all" || event?.type_event === ui.type;

      const matchLevel = ui.level === "all" || event?.level_event === ui.level;

      const matchStatus =
        ui.status === "all" ||
        (ui.status === "upcoming" && !eventIsPast) ||
        (ui.status === "past" && eventIsPast);

      const matchFavorite =
        !ui.favoriteOnly || favoriteEvents.includes(event.id);

      return (
        matchSearch &&
        matchCategory &&
        matchType &&
        matchLevel &&
        matchStatus &&
        matchFavorite
      );
    });

    list.sort((a, b) => {
      if (ui.sort === "title") {
        return collatorFR.compare(
          String(a?.titre || ""),
          String(b?.titre || "")
        );
      }

      const dateA = getEventDate(a?.start)?.getTime() || 0;
      const dateB = getEventDate(b?.start)?.getTime() || 0;

      if (ui.sort === "dateDesc") {
        return (
          dateB - dateA ||
          collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""))
        );
      }

      return (
        dateA - dateB ||
        collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""))
      );
    });

    return list;
  }, [events, favoriteEvents, ui]);

  const visibleEvents = useMemo(() => {
    return filteredEvents.slice(0, ui.perPage);
  }, [filteredEvents, ui.perPage]);

  const stats = useMemo(() => {
    const upcoming = events.filter((event) => !isEventPast(event)).length;
    const past = events.filter((event) => isEventPast(event)).length;
    const favorites = events.filter((event) =>
      favoriteEvents.includes(event.id)
    ).length;

    const types = new Set(
      events.map((event) => event?.type_event).filter(Boolean)
    ).size;

    return {
      total: events.length,
      upcoming,
      past,
      favorites,
      types,
    };
  }, [events, favoriteEvents]);

  const resetFilters = useCallback(() => {
    setDebouncedSearch("");
    persistUi(DEFAULT_UI_STATE);
  }, [persistUi]);

  const loadMore = useCallback(() => {
    persistUi({
      ...ui,
      perPage: ui.perPage + DEFAULT_UI_STATE.perPage,
    });
  }, [persistUi, ui]);

  const handleFavoriteChange = useCallback((eventId, nextValue) => {
    setFavoriteEvents((previous) => {
      const next = new Set(previous);

      if (nextValue) {
        next.add(eventId);
      } else {
        next.delete(eventId);
      }

      return Array.from(next);
    });
  }, []);

  return {
    loading,
    error,

    events,
    filteredEvents,
    visibleEvents,

    favoriteEvents,
    userId,

    ui,
    debouncedSearch,
    setDebouncedSearch,
    updateUi,
    resetFilters,
    loadMore,
    refresh,

    stats,

    typeOptions,
    categoryOptions,
    levelOptions,

    handleFavoriteChange,
  };
}